import { useEffect, useState } from "react";
import { Languages as LanguagesIcon, ArrowRightLeft, Globe, Loader2 } from "lucide-react";
import { fetchAdminSettings, adminUpdateSettings } from "@/lib/api";
import { toast } from "sonner";
import AdminTabsHub from "@/pages/admin/AdminTabsHub";
import AdminLanguages from "@/pages/admin/AdminLanguages";
import AdminTranslations from "@/pages/admin/AdminTranslations";

// Toggle that controls whether the public site shows the language switcher in the
// top menu. Hidden by default until an admin turns it on.
function TopMenuSwitcherToggle() {
  const [on, setOn] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAdminSettings().then((s) => setOn(!!s.show_language_switcher)).catch(() => setOn(false));
  }, []);

  const toggle = async () => {
    const next = !on;
    setOn(next);
    setSaving(true);
    try {
      await adminUpdateSettings({ show_language_switcher: next });
      toast(next ? "Language switcher is now visible in the top menu" : "Language switcher hidden from the top menu");
    } catch {
      setOn(!next);
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-6 flex items-center justify-between gap-4 border border-line bg-surface p-4" data-testid="lang-switcher-toggle-card">
      <div className="flex items-start gap-3">
        <Globe className="mt-0.5 h-5 w-5 text-saffron" />
        <div>
          <p className="text-sm font-medium text-ink">Show language switcher in the top menu</p>
          <p className="mt-0.5 text-xs text-ink-3">When off, visitors won't see the language dropdown in the header until you turn it on here.</p>
        </div>
      </div>
      <button
        data-testid="lang-switcher-toggle"
        onClick={toggle}
        disabled={on === null || saving}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${on ? "bg-saffron" : "bg-ink-3/40"} disabled:opacity-60`}
        aria-pressed={!!on}
      >
        {saving && <Loader2 className="absolute -left-6 h-4 w-4 animate-spin text-ink-3" />}
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}

export default function AdminLanguagesHub() {
  return (
    <div>
      <TopMenuSwitcherToggle />
      <AdminTabsHub
        idPrefix="languages"
        title="Languages & Translations"
        subtitle="Enable languages for the site and manage the field-level translations — together."
        tabs={[
          { key: "languages", label: "Languages", icon: LanguagesIcon, render: () => <AdminLanguages /> },
          { key: "translations", label: "Translations", icon: ArrowRightLeft, render: () => <AdminTranslations /> },
        ]}
      />
    </div>
  );
}
